import type { WorkflowFieldMeta } from '~/types/web-chat'

/**
 * A workflow definition's `inputs` → the field metadata shape chat renders (MAR-437 follow-up).
 *
 * Shared by every place that builds a chat card's fields from a definition —
 * `useConfirmCards.resolveWorkflowFields` (a fresh proposal) and `useWorkflowActions`'s rehydration
 * (restoring a card after reload) — so a field renders identically regardless of which path built it.
 *
 * The `template` slot is authored, not chosen — it resolves at run start from the platform rule.
 * Showing it would offer the user a decision that does not exist. The WHOLE `constraints` object
 * travels, not just `options`: `entitySource` (which party/field this input fills) lives there, and
 * dropping it is what silently disabled the client-record autofill early in this feature's life.
 */
export function workflowFieldsMeta(workflow: { inputs?: any[] } | null | undefined): WorkflowFieldMeta[] {
  const inputs: any[] = workflow?.inputs ?? []
  return inputs
    .filter(input => input?.type !== 'template')
    .map(input => ({
      key: input.key,
      label: String(input.label || input.key),
      type: String(input.type || 'text'),
      required: !!input.required,
      options: input.constraints?.options,
      constraints: input.constraints ?? undefined,
    }))
}

/**
 * Is this field value blank — undefined, null, '' or an empty array?
 *
 * The ONE copy of "is there a value at all" (MAR-948): the party blocks' `anyBlank`, the card's
 * "understood" summary and `useWorkflowActions`' submitted values ask this. Whether a REQUIRED field
 * is answered is a different question — `fieldComplete` in `workflowRunItems.ts`, built on this one,
 * which both the run form's gate and the chat card ask (`requestFieldComplete`, decided by Alex
 * 2026-09-25) — and `workflowFieldValues.test.ts` pins the three places the two disagree.
 */
export function isBlankValue(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)
}
